import React, { useState, useEffect} from 'react';
import {
  Box,
  Button,
  CopyToClipboard,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  Link,
  ExpandableSection,
  Popover
} from "@amzn/awsui-components-react";
import { getDynamicTablesForFlexico, getTableSyncMemory, triggerDynamicTableRefresh } from 'src/utilities/FlexicoAPIs';
import { useSelector } from 'react-redux'
import { createAuditLog } from '../Templates/Utils/AppActivityUtils';
import { captureMetrics } from 'src/katalMetrics/metricsHelperMethods';
import { ACTION_METHODS, METRIC_TYPE, SITE_SERVICES } from 'src/katalMetrics/metricsModels';

const FlexicoDynamicTableRefreshComponent = (props) => {
  const userId = useSelector((globalState) => globalState.auth.userId);
  const [tablesToRefreshDynamicInputs, setTablesToRefreshDynamicInputs] = useState({})
  const [dynamicTableSyncInfo, setDynamicTableSyncInfo] = useState(null)

  const tableStatusIndicatorTypeMap = {
    "Success" : "success",
    "Failed" : "error",
    "InProgress" : "in-progress",
    "Queued" : "info",
    null: "info",
  }

  useEffect(() => {
    if(props.refresh){
      setDynamicTableSyncInfo(null)
      setTablesToRefreshDynamicInputs({ ...tablesToRefreshDynamicInputs, loading: true, messageView: null })
      loadDynamicTables()
    }
    return () => {
      // this now gets called when the component unmounts
    };
  }, [props.refresh]);

  const getCurrentTime = () => {
        return Date.now();
    }

  function loadDynamicTables() {
    getDynamicTablesForFlexico(props.flex_id, function onSuccess(response) {
      if (response?.data?.getDynamicTablesForFlexico) {
        let responseObj = JSON.parse(response?.data?.getDynamicTablesForFlexico)
        if (responseObj?.hasOwnProperty("FAST")) {
          getTableSyncMemory(responseObj["FAST"], ["FAST"],
            function onSuccess(response) {
              let responseObj = JSON.parse(response?.data.getTableSyncMemory)
              setDynamicTableSyncInfo(responseObj?.[0]?.['results'])
            },
            function onFailure(error) {
            })
        }
        setTablesToRefreshDynamicInputs({ tables: JSON.parse(response.data.getDynamicTablesForFlexico), loading: false })
      }
    }, function onFailure(error) {
      setTablesToRefreshDynamicInputs({ loading: false, messageView: <StatusIndicator type='error'>Failed to fetch the tables!</StatusIndicator> })
    })
  }
  function addOrRemoveInRefreshList(sourceDatabase, tableNames) {
    let tempTablesToRefreshDynamicInputs = { ...tablesToRefreshDynamicInputs }
    if (!tempTablesToRefreshDynamicInputs.hasOwnProperty('markedToRefresh') || tempTablesToRefreshDynamicInputs['markedToRefresh'] == null) {
      tempTablesToRefreshDynamicInputs['markedToRefresh'] = {}
    }
    if (!tempTablesToRefreshDynamicInputs['markedToRefresh'].hasOwnProperty(sourceDatabase)) {
      tempTablesToRefreshDynamicInputs['markedToRefresh'][sourceDatabase] = []
    }
    tempTablesToRefreshDynamicInputs['markedToRefresh'][sourceDatabase] = tableNames
    setTablesToRefreshDynamicInputs(tempTablesToRefreshDynamicInputs)
  }

  function refreshDynamicTables() {
    setTablesToRefreshDynamicInputs({ ...tablesToRefreshDynamicInputs, loading: true, messageView: null })
    if (!tablesToRefreshDynamicInputs.hasOwnProperty('markedToRefresh') || tablesToRefreshDynamicInputs['markedToRefresh'] == null || tablesToRefreshDynamicInputs['markedToRefresh']?.length <= 0) {
      setTablesToRefreshDynamicInputs({ ...tablesToRefreshDynamicInputs, loading: false, messageView: <StatusIndicator type='warning'>No tables to refresh!</StatusIndicator> })
      return
    }
    let keys = Object.keys(tablesToRefreshDynamicInputs['markedToRefresh'])
    let foundAtleastOneTable = false
    for (let key of keys) {
      if (tablesToRefreshDynamicInputs['markedToRefresh'][key]?.length > 0) {
        foundAtleastOneTable = true
      }
    }
    if (!foundAtleastOneTable) {
      setTablesToRefreshDynamicInputs({ ...tablesToRefreshDynamicInputs, loading: false, messageView: <StatusIndicator type='warning'>No tables to refresh!</StatusIndicator> })
      return
    }

    // trigger refresh
    createAuditLog(`FLEXICO#${props.flex_id}`, `${getCurrentTime()}`, "Dynamic tables refresh request", JSON.stringify(tablesToRefreshDynamicInputs), JSON.stringify({}), "DYNAMIC TABLE REFRESH REQ", userId)
    const metricConfig = [{
                metricName : 'actionPerformedBy',
                metricValue : userId,
                metricType : METRIC_TYPE.STRING,
                isMonitor : true
            },
            {
                metricName : `refresh_trigger`,
                metricValue : props.flex_id,
                metricType : METRIC_TYPE.STRING,
                isMonitor : true
            },
            {
                metricName : 'button_name',
                metricValue : 'flexico_dynamic_input_refresh_trigger',
                metricType : METRIC_TYPE.STRING,
                isMonitor : true
            }
            ]
        if(tablesToRefreshDynamicInputs['markedToRefresh']){
          for(let source of Object.keys(tablesToRefreshDynamicInputs['markedToRefresh'])){
            for(let table of tablesToRefreshDynamicInputs['markedToRefresh'][source]){
              metricConfig.push({
                metricName : 'refresh_table',
                metricValue : table,
                metricType : METRIC_TYPE.STRING,
            })
            }
          }
        }

    captureMetrics(ACTION_METHODS.BUTTON_CLICK, metricConfig, SITE_SERVICES.FLEXICO)

    triggerDynamicTableRefresh(tablesToRefreshDynamicInputs['markedToRefresh'], `APP_${props.app_id}_FLEXICO_${props.flex_id}`, function onSuccess(response) {
      let responseObj = JSON.parse(response?.data?.triggerDynamicTableRefresh)
      if (responseObj?.type === 'SUCCESS') {
        createAuditLog(`FLEXICO#${props.flex_id}`, `${getCurrentTime()}`, "Dynamic tables refresh request done", JSON.stringify({}), JSON.stringify(responseObj), "DYNAMIC TABLE REFRESH REQ DONE", userId)
        let tempTablesToRefreshDynamicInputs = { ...tablesToRefreshDynamicInputs }
        tempTablesToRefreshDynamicInputs['markedToRefresh'] = null
        setTablesToRefreshDynamicInputs({
          ...tempTablesToRefreshDynamicInputs, loading: false, messageView: <Box>
            <StatusIndicator type='success'>Refresh initiated</StatusIndicator>
            <Box>Execution id: {responseObj?.execution_name}<CopyToClipboard
              copyButtonAriaLabel="Copy execution id"
              copyErrorText="Id failed to copy"
              copySuccessText="Id copied"
              textToCopy={responseObj?.execution_name}
              variant="icon"
            /></Box>
          </Box>
        })
      } else {
        setTablesToRefreshDynamicInputs({ ...tablesToRefreshDynamicInputs, loading: false, messageView: <StatusIndicator type='error'>Refresh failed!</StatusIndicator> })
      }
    }, function onFailure(response) {
      setTablesToRefreshDynamicInputs({ ...tablesToRefreshDynamicInputs, loading: false, messageView: <StatusIndicator type='error'>Refresh failed!</StatusIndicator> })
    })
  }

  return (
    <SpaceBetween direction="vertical" size="m" key={props.flex_id}>
      <SpaceBetween direction="vertical" size="m">
        {
          tablesToRefreshDynamicInputs?.messageView && <>{tablesToRefreshDynamicInputs?.messageView}</>
        }
        {
          tablesToRefreshDynamicInputs?.infoMessage && <span className="last_updated">{tablesToRefreshDynamicInputs?.infoMessage}</span>
        }
      </SpaceBetween>
      {
        tablesToRefreshDynamicInputs?.['loading'] && <Spinner>Loading..</Spinner>
      }
      {
        tablesToRefreshDynamicInputs?.['tables'] && Object.keys(tablesToRefreshDynamicInputs?.['tables']).map((value, index) => {
          return <Box key={index}>
            {
              <Table
                onSelectionChange={({ detail }) =>
                  addOrRemoveInRefreshList(value, detail.selectedItems)
                }
                selectedItems={tablesToRefreshDynamicInputs?.['markedToRefresh']?.[value]}
                columnDefinitions={[
                  {
                    id: "table",
                    header: "Table",
                    cell: item => <Link href="#" key={item?item : '1'}>{item}</Link>,
                    sortingField: "name",
                    isRowHeader: true
                  },
                  {
                    id: "last_synced",
                    header: "Last synced(local)",
                    cell: item => dynamicTableSyncInfo ? <Box>{dynamicTableSyncInfo[item]?.['last_sync_success_time_stamp']? new Date(dynamicTableSyncInfo[item]?.['last_sync_success_time_stamp'])?.toLocaleString() || '' : ''}</Box> : <StatusIndicator type="loading">Loading information..</StatusIndicator>,
                    sortingField: "name",
                    isRowHeader: true
                  },
                  {
                    id: "dependants",
                    header: "Dependants",
                    resizableColumns: true,
                    cell: item => <ExpandableSection defaultExpanded={true} headerText={<Box>Flexico(s)</Box>}>
                      <SpaceBetween direction='horizontal' size='s'>{
                        dynamicTableSyncInfo ? dynamicTableSyncInfo[item]?.['dependants']?.map((value, index) => {
                          return <Link external href={`/App/${value.app_id}/flexico/${value.flex_id}`}>{value.label}</Link>
                        }) : <StatusIndicator type="loading">Loading information..</StatusIndicator>
                      }
                    
                    </SpaceBetween>
                    </ExpandableSection>  ,
                    sortingField: "name",
                    isRowHeader: true
                  },
                  {
                    id: "status",
                    header: "Status",
                    resizableColumns: true,
                    cell: item => dynamicTableSyncInfo ? <SpaceBetween direction='horizontal' size='s'>
                      <StatusIndicator type={tableStatusIndicatorTypeMap[dynamicTableSyncInfo?.[item]?.['latest_status']] || 'info'}><Popover
                        dismissButton={false}
                        position="top"
                        triggerType="custom"
                        content={
                          <SpaceBetween>
                            <Box>Last sync initiated : {new Date(dynamicTableSyncInfo?.[item]?.['last_sync_time_stamp'])?.toLocaleString() || ''}</Box>
                          </SpaceBetween>
                        }
                      >
                        {dynamicTableSyncInfo?.[item]?.['latest_status'] || ''}
                      </Popover></StatusIndicator>


                    </SpaceBetween> : <StatusIndicator type="loading">Loading information..</StatusIndicator>,
                    sortingField: "name",
                    isRowHeader: true
                  }
                ]}
                columnDisplay={[
                  { id: "table", visible: true },
                  { id: "dependants", visible: true },
                  { id: "last_synced", visible: true },
                  { id: "status", visible: true }
                ]}
                items={tablesToRefreshDynamicInputs?.['tables'][value]}
                selectionType="multi"
                header={
                  <Header
                    counter={
                      tablesToRefreshDynamicInputs?.['markedToRefresh']?.[value]?.length
                        ? "(" + tablesToRefreshDynamicInputs?.['markedToRefresh']?.[value].length + `/${tablesToRefreshDynamicInputs?.['tables']?.[value]?.length})`
                        : `${tablesToRefreshDynamicInputs?.['tables']?.[value]?.length || 0}`
                    }
                  >
                    {value} tables
                  </Header>
                }
              >

              </Table>
            }
          </Box>;
        })
      }
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="primary" disabled={tablesToRefreshDynamicInputs?.['loading']} onClick={() => refreshDynamicTables()}>Refresh</Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  )
}

export default FlexicoDynamicTableRefreshComponent;